<template>
  <div>
    <transition name="slideUp">
      <div class="chatContainer" v-if="open">
        <div class="chatWindow">
          <font-awesome-icon @click="$emit('close')" class="closeButton" :icon="['fas', 'xmark']" />
          
          <!-- Chat Interface for Eligible Users -->
          <template v-if="canAccessAIChat">
            <div class="chatHeader" style="margin-top: 60px;">
              <img src="/static/img/KovoLogoWhite.svg" alt="" class="guardianIcon">
              <div class="chatHeaderText">
                <h5>Hello,<br>I'm Kovo!</h5>
                <h6>Your friendly Firewall Assistant</h6>
              </div>
            </div>
            <div class="chatMessages">
              <div v-for="(message, i) of messages.slice().reverse()" v-bind:key="i" class="messageContainer" :class="message.role + 'Container'">
                <i v-if="message.role === 'loading'" class="el-icon-loading loadingIcon"></i>
                <div v-else-if="message.role === 'ai'">
                  <img class="aiImage" src="/static/img/KLogo.svg" alt="">
                  <br>
                  <font-awesome-icon @click="openReport" v-if="message.message !== 'How can I help you?'" class="flagIcon" :icon="['fas', 'flag']" />
                </div>
                <img v-else-if="currentUser.image" class="profileImage" :src="currentUser.image" alt="ProfilePicture">
                <div v-else class="profileIconWrapper">
                  <font-awesome-icon class="profileIcon" :icon="['far', 'user']"/>
                </div>
                <div v-if="message.message" class="message" :class="message.role + 'Message'" :id="i + 'message'">
                  {{message.message}}
                </div>
              </div>
            </div>
            <div class="chatFooter">
              <input class="chatText" type="text" v-model="userInput" placeholder="Send a Message..." @keyup.enter="submitMessage">
              <button class="sendButton"><font-awesome-icon :icon="['fas', 'paper-plane']" @click="submitMessage"/></button>
            </div>
          </template>
          
          <!-- Upgrade Overlay for Free Users -->
          <div v-else class="upgrade-overlay">
            <div class="upgrade-content">
              <div class="ai-hero">
                <div class="ai-icon">
                  <font-awesome-icon :icon="['fas', 'robot']" />
                </div>
                <h2 class="ai-title">Meet Kovo</h2>
                <p class="ai-subtitle">Your AI Network Assistant</p>
              </div>
              
              <div class="features-showcase">
                <h3 class="features-title">Unlock Intelligent Network Management</h3>
                <div class="features-grid">
                  <div class="feature-item">
                    <font-awesome-icon :icon="['fas', 'laptop']" class="feature-icon" />
                    <span class="feature-text">List and manage connected devices</span>
                  </div>
                  <div class="feature-item">
                    <font-awesome-icon :icon="['fas', 'ban']" class="feature-icon" />
                    <span class="feature-text">Block websites and URLs instantly</span>
                  </div>
                  <div class="feature-item">
                    <font-awesome-icon :icon="['fas', 'shield-alt']" class="feature-icon" />
                    <span class="feature-text">Monitor network activity and threats</span>
                  </div>
                  <div class="feature-item">
                    <font-awesome-icon :icon="['fas', 'chart-line']" class="feature-icon" />
                    <span class="feature-text">Get intelligent network insights</span>
                  </div>
                  <div class="feature-item">
                    <font-awesome-icon :icon="['fas', 'cog']" class="feature-icon" />
                    <span class="feature-text">Automated firewall management</span>
                  </div>
                  <div class="feature-item">
                    <font-awesome-icon :icon="['fas', 'comments']" class="feature-icon" />
                    <span class="feature-text">Answer questions about your network</span>
                  </div>
                </div>
              </div>
              
              <div class="upgrade-actions">
                <button class="upgrade-btn" @click="showUpgradeModal('AI_CHAT')">
                  <font-awesome-icon :icon="['fas', 'rocket']" class="btn-icon" />
                  Upgrade to Device Plan
                </button>
                <p class="upgrade-note">Requires device subscription to access AI assistant</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
    <MessageModal :message="appMessage.text" :type="appMessage.type" ref="messageModal" />
    <BaseModal
        :visible="reportOpen"
        title="Report AI Response"
        :cancel-show="false"
        @submit="submitReport"
        @close="closeReport"
        :submit-disabled="!selectedReason"
    >
      Report Reason
      <br>
      <CustomSelect :options="reasonCodes" v-model="selectedReason"></CustomSelect>
      <br>
      Please add an explanation to this report so we can better address the issue.
      <br>
      <textarea type="text" v-model="reportText"></textarea>
    </BaseModal>
  </div>
</template>

<script>

import {useLoginStore} from "@/stores/loginStore";
import MessageModal from "@/components/MessageModal.vue";
import DeviceAPIService from "@/servicehandlers/DeviceAPIService";
import BaseModal from "@/components/BaseModal.vue";
import APIService from "@/servicehandlers/APIService";
import CustomSelect from "@/components/CustomSelect.vue";
import SubscriptionPermissionService from "@/services/SubscriptionPermissionService";
const deviceAPIService = new DeviceAPIService()
const apiService = new APIService()

export default {
  name: 'ChatPage',
  components: {CustomSelect, BaseModal, MessageModal},
  props: {
    open: {
      type: Boolean,
      required: true,
    }
  },
  data(){
    return {
      appMessage: {
        text: '',
        type: 'error',
      },
      messages: [
        {
          role: 'ai',
          message: 'How can I help you?',
        },
      ],
      currentUser: useLoginStore().getUser,
      userInput: '',
      reportOpen: false,
      reportText: '',
      reasonCodes: [],
      selectedReason: null,
    }
  },
  computed: {
    subscriptionType() {
      return useLoginStore().getSubscriptionType
    },
    isDeviceOnline() {
      return useLoginStore().getDeviceOnline
    },
    canAccessAIChat() {
      return SubscriptionPermissionService.canAccessFeature(
        this.subscriptionType,
        'AI_CHAT',
        this.isDeviceOnline
      )
    }
  },
  mounted(){
    //console.clear()
    this.getReasonCodes()
  },
  methods: {
    showError(e){
      this.appMessage.text = 'Error: ' + e
      this.appMessage.type = 'error'
      this.$refs.messageModal.open()
    },
    async getReasonCodes(){
      let response = await apiService.getReasonCodes(this.$router, useLoginStore())
      for (let reason of response.reason_codes) {
        const capReason = reason
            .split(' ')
            .map(word =>
                word.charAt(0).toUpperCase() + word.slice(1)
            )
            .join(' ');

        this.reasonCodes.push({id: reason, name: capReason})
      }
    },
    async submitMessage(){
      // Check if user has access to AI chat
      if (!this.canAccessAIChat) {
        this.showUpgradeModal('AI_CHAT')
        return
      }
      
      if (useLoginStore().getSubscriptionType === 'device' && useLoginStore().getDeviceOnline) {
        try {
          if (this.userInput.length > 0) {
            let userMessage = {
              role: 'user',
              message: this.userInput,
            }
            this.userInput = ''

            this.messages.push(userMessage)

            this.messages.push({
              role: 'loading'
            })

            let aiPayload = {
              "text": userMessage.message,
              "key": "1",
            }
            let aiResponse = await deviceAPIService.aiCall(aiPayload, this.$router, useLoginStore())

            let response = {
              role: 'ai',
              message: aiResponse.action_result
            }

            if (response.message) {
              this.messages.push(response)
            }

            for (let message of this.messages) {
              if (message.role === 'loading') {
                let index = this.messages.indexOf(message)
                this.messages.splice(index, 1)
              }
            }
          }
        } catch (e) {
          this.showError(e)
          for (let message of this.messages) {
            if (message.role === 'loading') {
              let index = this.messages.indexOf(message)
              this.messages.splice(index, 1)
            }
          }
        }
      } else {
        this.appMessage.text = "Device Offline"
        this.appMessage.type = 'info'
        this.$refs.messageModal.open()
      }

    },
    openReport(){
      this.reportOpen = true
    },
    async submitReport(){
      try {
        let payload = {
          reason_code: this.reasonCodes.indexOf(this.reasonCodes.find(data => data.id === this.selectedReason)),
          json: this.messages,
          report: this.reportText,
        }

        await apiService.submitAIReport(payload, this.$router, useLoginStore())

        this.appMessage.text = 'Successfully submitted AI report!!'
        this.appMessage.type = 'success'
        this.$refs.messageModal.open()

        this.closeReport()
      } catch (e) {
        this.showError(e)
      }
    },
    closeReport(){
      this.reportOpen = false
      this.reportText = ''
      this.selectedReason = null
    },
    showUpgradeModal(featureName) {
      this.$router.push({
        name: 'upgrade',
        query: {
          feature: featureName,
          return: this.$route.path
        }
      })
    },
  },
}
</script>

<style scoped>
.flagIcon{
  color: var(--text-primary);
  font-size: 14px;
}
.loadingIcon {
  color: var(--text-primary);
  border-radius: 50%;
  font-size: 50px;
}
.profileImage{
  width: 43px;
  height: 43px;
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  border: 1px solid white;
}
.aiImage{
  width: 43px;
  height: 43px;
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  border: 1px solid var(--color-secondary-button);
  background-color: var(--color-secondary-button);
  padding: 10px;
}
.profileIconWrapper {
  width: 43px;
  height: 43px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.profileIcon{
  width: 30px;
  height: 30px;
  aspect-ratio: 1 / 1;
  color: var(--permanent-white);
}
.aiMessage, .userMessage {
  padding: 15px;
  border-radius: 9px;
  font-size: 12px;
  max-width: 60%;
}
.aiMessage{
  background-color: var(--color-background-ai-message);
}
.userMessage{
  background-color: var(--color-background-user-message);
}
.messageContainer{
  padding: 7.5px 15px;
  display: flex;
  flex-direction: row;
  gap: 15px;
}
.userContainer {
  flex-direction: row-reverse;
}
.aiContainer {
  flex-direction: row;
}
.loadingContainer {
  justify-content: center;
}
.chatText{
  font-size: 12px;
}
.sendButton{
  background-color: transparent;
  border-color: transparent;
  font-size: 30px;
  color: var(--text-primary);
  padding: 0;
}
.chatHeaderText{
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 7.5px;
}
.guardianIcon {
  /* much larger “G” */
  width: 64px;
  height: 64px;
  aspect-ratio: 1/1;
  /* green background box */
  background-color: var(--color-secondary-button);
  /* border to match */
  border: 2px solid var(--color-secondary-button-border);
  border-radius: 4px;
  /* center the SVG */
  object-fit: contain;
  display: block;
  padding: 7px;
  margin-bottom: 12px;
  margin-left: 20px;
}
.chatHeader {
  /* align icon and text side-by-side, flush left */
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;                /* space between G and text */
  padding: 1rem;            /* adjust as needed */
}

.chatMessages{
  height: calc(100% - 150px - 70px);
  max-height: calc(100% - 150px - 70px);
  overflow-y: auto;
  display: flex;
  flex-direction: column-reverse;
  position: relative;
}
.chatFooter{
  padding: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  background: #413935;
  height: 70px;
  width: 100%;
}
h5{
  text-align: left;
}
.chatWindow{
  position: relative;
}
.chatContainer{
  width: 100vw;
  height: calc(100vh - var(--header-height));
  background-color: var(--color-background);
  position: absolute;
  top: var(--header-height);
  left: 0;
  z-index: 100;
}
.chatWindow{
  width: 100%;
  height: 100%;
  background-color: var(--color-card);
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  border: 1px solid var(--color-card-border);
}

/* slide-in/out animation */
.slideUp-enter-active,
.slideUp-leave-active {
  transition: transform .3s ease;
}
.slideUp-enter {
  transform: translateY(100%);
}
.slideUp-enter-to {
  transform: translateY(0);
}
.slideUp-leave {
  transform: translateY(0);
}
.slideUp-leave-to {
  transform: translateY(100%);
}

/* Upgrade Overlay Styles */
.upgrade-overlay {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--network-health-gradient);
  position: relative;
  overflow: hidden;
}

.upgrade-overlay::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3), transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1), transparent 50%);
  pointer-events: none;
}

.upgrade-content {
  text-align: center;
  color: white;
  max-width: 600px;
  padding: 40px 20px;
  position: relative;
  z-index: 2;
}

.ai-hero {
  margin-bottom: 40px;
}

.ai-icon {
  font-size: 4rem;
  margin-bottom: 20px;
  opacity: 0.9;
  color: var(--text-primary);
}

.ai-title {
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 10px;
  font-family: 'Oxanium', 'Inter', sans-serif;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.ai-subtitle {
  font-size: 1.25rem;
  opacity: 0.9;
  margin-bottom: 0;
  font-weight: 300;
}

.features-showcase {
  margin-bottom: 40px;
}

.features-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 30px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  max-width: 500px;
  margin: 0 auto;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}

.feature-item:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-2px);
}

.feature-icon {
  font-size: 1.25rem;
  opacity: 0.9;
  flex-shrink: 0;
}

.feature-text {
  font-size: 0.875rem;
  font-weight: 500;
  text-align: left;
  line-height: 1.4;
}

.upgrade-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}

.upgrade-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px 30px;
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50px;
  color: white;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.upgrade-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}

.btn-icon {
  font-size: 1rem;
}

.upgrade-note {
  font-size: 0.875rem;
  opacity: 0.8;
  margin: 0;
  font-style: italic;
}

/* Mobile responsive adjustments */
@media (max-width: 768px) {
  .upgrade-content {
    padding: 30px 15px;
  }
  
  .ai-icon {
    font-size: 3rem;
  }
  
  .ai-title {
    font-size: 2.25rem;
  }
  
  .ai-subtitle {
    font-size: 1.125rem;
  }
  
  .features-title {
    font-size: 1.25rem;
  }
  
  .features-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .feature-item {
    padding: 12px;
    color: var(--text-primary);
  }
  
  .feature-text {
    font-size: 0.8rem;
  }
  
  .upgrade-btn {
    padding: 12px 25px;
    font-size: 1rem;
  }
}

@media (max-width: 480px) {
  .upgrade-content {
    padding: 20px 10px;
  }
  
  .ai-icon {
    font-size: 2.5rem;
  }
  
  .ai-title {
    font-size: 1.875rem;
  }
  
  .ai-subtitle {
    font-size: 1rem;
  }
  
  .features-title {
    font-size: 1.125rem;
  }
  
  .upgrade-btn {
    padding: 10px 20px;
    font-size: 0.9rem;
    color: var(--text-primary);
  }
}
</style>
